var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import utils, { isIOSBrowser } from "./utils/index";
import { BehaviorType, CollectorId, PlasmState, } from "./types/index";
import SnapshotsCollector from "./collectors/SnapshotsCollector";
import ScreenshotsCollector from "./collectors/ScreenshotsCollector";
import { CollectorSetupResultStatus } from "./types/collector-setup-result";
import NavigationCollector from "./collectors/NavigationCollector";
import ClickBehavior from "./behaviors/ClickBehavior";
import CopyBehavior from "./behaviors/CopyBehavior";
import PageLoadBehavior from "./behaviors/PageLoadBehavior";
import OnlineBehavior from "./behaviors/OnlineBehavior";
import OfflineBehavior from "./behaviors/OfflineBehavior";
import BlurBehavior from "./behaviors/BlurBehavior";
import FocusBehavior from "./behaviors/FocusBehavior";
import BeforeUnloadBehavior from "./behaviors/BeforeUnloadBehavior";
import ContextMenuBehavior from "./behaviors/ContextMenuBehavior";
import ChangeBehavior from "./behaviors/ChangeBehavior";
import CutBehavior from "./behaviors/CutBehavior";
import PasteBehavior from "./behaviors/PasteBehavior";
import DoubleClickBehavior from "./behaviors/DoubleClickBehavior";
import DragBehavior from "./behaviors/DragBehavior";
import SelectBehavior from "./behaviors/SelectBehavior";
import ResizeBehavior from "./behaviors/ResizeBehavior";
import ScrollBehavior from "./behaviors/ScrollBehavior";
import MouseOutBehavior from "./behaviors/MouseOutBehavior";
import KeyBehavior from "./behaviors/KeyBehavior";
import UnfocusBehavior from "./behaviors/UnfocusBehavior";
import IframeBehaviorCollector from "./types/IframeBehaviorCollector";
import { Sentry } from "./utils/Sentry";
import { Logger } from "./utils/Logger";
import { PlasmIntegrationError } from "./errrors/PlasmIntegrationError";
import { PlasmInternalError } from "./errrors/PlasmInternalError";
import MouseSpeedBehavior from "./behaviors/MouseSpeedBehavior";
import KeystrokeSpeedBehavior from "./behaviors/KeystrokeSpeedBehavior";
import IOSContextMenuBehavior from "./behaviors/IOSContextMenuBehavior";
import QuestionShownBehavior from "./behaviors/QuestionShownBehavior";
class Plasm {
    constructor(options) {
        this.logger = new Logger("Plasm");
        this.state = PlasmState.notready;
        this.configuredCollectors = [];
        this.configuredBehaviors = [];
        this.behaviorData = [];
        this.iframeBehaviorListeners = [];
        try {
            Plasm.validateOptions(options);
            this.options = options;
            this.supportedCollectors = Plasm.getSupportedCollectors();
            if (options.sessionUuid) {
                this.sessionUuid = options.sessionUuid;
            }
        }
        catch (err) {
            this.handleExceptionInPublicApi(err);
        }
    }
    handleExceptionInPublicApi(err) {
        const isHandledError = err instanceof PlasmInternalError && err.isHandled;
        if (!isHandledError) {
            this.logger.error(err);
        }
        if (err instanceof PlasmIntegrationError || isHandledError) {
            throw err;
        }
        Sentry.captureError(err);
        const handledError = new PlasmInternalError({ code: "general" });
        handledError.isHandled = true;
        throw handledError;
    }
    init() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                this.config = yield this.fetchConfig();
                this.setApplicationMetadata();
                this.initSentrySafely();
            }
            catch (err) {
                this.handleExceptionInPublicApi(err);
            }
        });
    }
    initSentrySafely() {
        if (this.config.sentryDSN != null) {
            try {
                Sentry.init({
                    buildVersion: this.config.buildVersion,
                    sentryDSN: this.config.sentryDSN,
                    env: this.config.sentryEnv,
                    integrationApp: this.options.appName,
                });
            }
            catch (err) {
                this.logger.error("Sentry setup failed", err);
            }
        }
    }
    static validateOptions(options) {
        if (options.expiresIn != null && options.expiresIn < 1) {
            throw new PlasmIntegrationError({
                code: "invalid_input",
                msg: '"expiresIn" should be greater than or equal 1',
            });
        }
    }
    static getSupportedCollectors() {
        const supportedCollectors = [];
        Object.keys(CollectorId).forEach((id) => {
            var _a, _b;
            switch (id) {
                case CollectorId.camera:
                    if (((_a = navigator === null || navigator === void 0 ? void 0 : navigator.mediaDevices) === null || _a === void 0 ? void 0 : _a.getUserMedia) !== undefined) {
                        supportedCollectors.push(id);
                    }
                    break;
                case CollectorId.screen:
                    if (((_b = navigator === null || navigator === void 0 ? void 0 : navigator.mediaDevices) === null || _b === void 0 ? void 0 : _b.getDisplayMedia) !== undefined ||
                        utils.isAccessedFromMobileApp()) {
                        supportedCollectors.push(id);
                    }
                    break;
                case CollectorId.navigation:
                    supportedCollectors.push(id);
                    break;
                default:
            }
        });
        return supportedCollectors;
    }
    setupCollector(id, options) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                yield this.stopCollectorIfRunning(id);
                const collector = this.createCollector(id, options);
                const setupResult = yield collector.setup();
                if (setupResult.status === CollectorSetupResultStatus.success) {
                    this.configuredCollectors.push(collector);
                }
                return setupResult;
            }
            catch (err) {
                this.handleExceptionInPublicApi(err);
            }
        });
    }
    stopCollectorIfRunning(id) {
        return __awaiter(this, void 0, void 0, function* () {
            const configuredCollector = this.configuredCollectors.find((collector) => collector.id === id);
            if (configuredCollector != null) {
                yield configuredCollector.stop();
                this.configuredCollectors = this.configuredCollectors.filter((collector) => collector.id !== id);
            }
        });
    }
    createCollector(id, options) {
        switch (id) {
            case CollectorId.camera:
                return new SnapshotsCollector(options, this);
            case CollectorId.screen:
                return new ScreenshotsCollector(options, this);
            case CollectorId.navigation:
                return new NavigationCollector(options, this);
            default:
                throw new PlasmIntegrationError({ msg: "unknown collector id" });
        }
    }
    getReady() {
        return __awaiter(this, void 0, void 0, function* () {
            this.validateStart();
            try {
                yield Promise.all(this.configuredCollectors.map((collector) => collector.getReady()));
                this.state = PlasmState.stopped;
            }
            catch (err) {
                this.handleExceptionInPublicApi(err);
            }
        });
    }
    startCollectors(startOptions) {
        return __awaiter(this, void 0, void 0, function* () {
            yield Promise.all(this.configuredCollectors.map((collector) => collector.start()));
            this.state = PlasmState.started;
            this.debug(`started. collectors: [${this.configuredCollectors.map((c) => c.id).join(", ")}]`);
            const { appName } = this.config;
            if (startOptions != null && startOptions.collectibles != null) {
                const { collectibles, addMetadata, questionTracking, collectGeometry } = startOptions;
                this.startBehavior(collectibles, appName, addMetadata, startOptions.iframesUsed, questionTracking, collectGeometry);
            }
        });
    }
    clearIntervalAndTimeoutStartChecker() {
        if (this.startCheckerTimeoutId != null) {
            clearTimeout(this.startCheckerTimeoutId);
        }
        if (this.startCheckerIntervalId != null) {
            clearInterval(this.startCheckerIntervalId);
        }
    }
    startWithChecker(startOptions) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.state === PlasmState.started) {
                return;
            }
            const { startChecker } = this.options;
            this.startCheckerTimeoutId = setTimeout(() => __awaiter(this, void 0, void 0, function* () {
                if (this.state !== PlasmState.started) {
                    yield this.startCollectors(startOptions);
                    this.clearIntervalAndTimeoutStartChecker();
                }
            }), startChecker.timeout);
            this.startCheckerIntervalId = setInterval(() => __awaiter(this, void 0, void 0, function* () {
                if (this.state !== PlasmState.started && startChecker.isCollectorsCanStartWithInterval()) {
                    yield this.startCollectors(startOptions);
                    this.clearIntervalAndTimeoutStartChecker();
                }
            }), startChecker.interval);
        });
    }
    start(options) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                if (options != null) {
                    this.updateOptions(options);
                }
                yield this.prepareStart();
                switch (this.state) {
                    case PlasmState.notready:
                        this.logger.warn('not yet ready. You need to call "getReady" prior to "start"');
                        break;
                    case PlasmState.started:
                        this.logger.warn("already started");
                        break;
                    case PlasmState.stopped: {
                        if (this.options.startChecker == null) {
                            yield this.startCollectors(options);
                        }
                        else {
                            yield this.startWithChecker(options);
                        }
                        break;
                    }
                    default:
                }
                return this.sessionUuid;
            }
            catch (err) {
                this.handleExceptionInPublicApi(err);
            }
        });
    }
    updateOptions(options) {
        const { sessionUuid, snapshotInterval, screenshotInterval } = options;
        if (sessionUuid != null) {
            this.sessionUuid = sessionUuid;
        }
        this.configuredCollectors.forEach((collector) => {
            if (collector.id === CollectorId.camera && snapshotInterval != null) {
                collector.setOptions({ interval: snapshotInterval });
            }
            if (collector.id === CollectorId.screen && screenshotInterval != null) {
                collector.setOptions({ interval: screenshotInterval });
            }
        });
    }
    prepareStart() {
        return __awaiter(this, void 0, void 0, function* () {
            this.validateStart();
            yield this.prepareSession();
        });
    }
    validateStart() {
        if (this.configuredCollectors.length === 0) {
            throw new PlasmIntegrationError({ msg: "failed to start: no configured collectors" });
        }
    }
    prepareSession() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.sessionUuid === undefined) {
                const session = yield this.createSessionRequest();
                this.sessionUuid = session.uuid;
            }
        });
    }
    fetchConfig() {
        return __awaiter(this, void 0, void 0, function* () {
            const url = `${this.options.plasmApiUrl}/config`;
            return utils.request({
                authToken: this.options.clientToken,
                method: "GET",
                url,
            });
        });
    }
    stop() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.state === PlasmState.stopped) {
                return;
            }
            try {
                this.configuredCollectors.forEach((collector) => collector.stop());
                this.stopBehavior();
                this.state = PlasmState.stopped;
                this.debug("Plasm stopped");
            }
            catch (err) {
                this.handleExceptionInPublicApi(err);
            }
        });
    }
    createSessionRequest() {
        return __awaiter(this, void 0, void 0, function* () {
            const payload = { expiresIn: this.options.expiresIn };
            const url = `${this.options.plasmApiUrl}/sessions`;
            return utils.request({
                authToken: this.options.clientToken,
                method: "POST",
                url,
                payload,
            });
        });
    }
    debug(message) {
        if (this.options.debug) {
            this.logger.debug(message);
        }
    }
    startBehavior(collectibles, appName, addMetadata, iframesUsed, questionTracking, collectGeometry) {
        if (this.configuredBehaviors.length > 0) {
            this.configuredBehaviors.forEach((behavior) => behavior.stop());
        }
        this.configuredBehaviors = [];
        collectibles.forEach((collect) => {
            let behavior = null;
            switch (collect) {
                case BehaviorType.click:
                    behavior = new ClickBehavior(this, appName, addMetadata, collectGeometry === null || collectGeometry === void 0 ? void 0 : collectGeometry.clickTargets);
                    break;
                case BehaviorType.dblclick:
                    behavior = new DoubleClickBehavior(this, appName, addMetadata);
                    break;
                case BehaviorType.copy:
                    behavior = new CopyBehavior(this, appName, addMetadata);
                    break;
                case BehaviorType.cut:
                    behavior = new CutBehavior(this, appName, addMetadata);
                    break;
                case BehaviorType.paste:
                    behavior = new PasteBehavior(this, appName, addMetadata);
                    break;
                case BehaviorType.load:
                    behavior = new PageLoadBehavior(this, appName, addMetadata);
                    break;
                case BehaviorType.online:
                    behavior = new OnlineBehavior(this, appName, addMetadata);
                    break;
                case BehaviorType.offline:
                    behavior = new OfflineBehavior(this, appName, addMetadata);
                    break;
                case BehaviorType.blur:
                    behavior = new BlurBehavior(this, appName, addMetadata);
                    break;
                case BehaviorType.focus:
                    behavior = new FocusBehavior(this, appName, addMetadata);
                    break;
                case BehaviorType.beforeunload:
                    behavior = new BeforeUnloadBehavior(this, appName, addMetadata);
                    break;
                case BehaviorType.contextmenu:
                    behavior = isIOSBrowser()
                        ? new IOSContextMenuBehavior(this, appName, addMetadata)
                        : new ContextMenuBehavior(this, appName, addMetadata);
                    break;
                case BehaviorType.change:
                    behavior = new ChangeBehavior(this, appName, addMetadata);
                    break;
                case BehaviorType.drag:
                    behavior = new DragBehavior(this, appName, addMetadata);
                    break;
                case BehaviorType.select:
                    behavior = new SelectBehavior(this, appName, addMetadata);
                    break;
                case BehaviorType.resize:
                    behavior = new ResizeBehavior(this, appName, addMetadata);
                    break;
                case BehaviorType.scroll:
                    behavior = new ScrollBehavior(this, appName, addMetadata);
                    break;
                case BehaviorType.mouseout:
                    behavior = new MouseOutBehavior(this, appName, addMetadata);
                    break;
                case BehaviorType.key:
                    behavior = new KeyBehavior(this, appName, addMetadata);
                    break;
                case BehaviorType.unfocus: {
                    behavior = new UnfocusBehavior(this, appName, addMetadata);
                    const navigatorCollector = this.configuredCollectors.find((collector) => collector.id === CollectorId.navigation);
                    if (navigatorCollector != null) {
                        navigatorCollector.setPubSubEventCollector(behavior);
                    }
                    break;
                }
                case BehaviorType.mousespeed:
                    behavior = new MouseSpeedBehavior(this, appName, addMetadata);
                    break;
                case BehaviorType.keystrokespeed:
                    behavior = new KeystrokeSpeedBehavior(this, appName, addMetadata);
                    break;
                case BehaviorType.question_shown:
                    if (questionTracking == null) {
                        this.debug("question_shown skipped. questionTracking option not available");
                        break;
                    }
                    if (typeof IntersectionObserver === "undefined") {
                        this.debug("question_shown skipped. IntersectionObserver not available");
                        break;
                    }
                    behavior = new QuestionShownBehavior(this, appName, questionTracking, addMetadata);
                    break;
                default:
                    throw new PlasmIntegrationError({ msg: "unknown behavior id" });
            }
            if (behavior != null) {
                behavior.start();
                this.configuredBehaviors.push(behavior);
                if (iframesUsed && behavior.iframeBehaviorListeners.length > 0) {
                    this.iframeBehaviorListeners = [
                        ...this.iframeBehaviorListeners,
                        ...behavior.iframeBehaviorListeners,
                    ];
                }
            }
        });
        if (iframesUsed && this.iframeBehaviorListeners.length > 0) {
            this.iframeBehaviorCollector = new IframeBehaviorCollector(this, this.iframeBehaviorListeners);
            this.iframeBehaviorCollector.start();
        }
    }
    stopBehavior() {
        this.configuredBehaviors.forEach((behavior) => behavior.stop());
        if (this.iframeBehaviorCollector != null) {
            this.iframeBehaviorCollector.stop();
        }
    }
    createSnapshot(data) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.state !== PlasmState.started) {
                throw new PlasmIntegrationError({ msg: "failed create snapshot. Plasm not started" });
            }
            try {
                const snapshotsCollector = this.configuredCollectors.find((collector) => collector.id === CollectorId.camera);
                if (snapshotsCollector == null) {
                    throw new PlasmIntegrationError({ msg: "snapshot collector not configured" });
                }
                yield snapshotsCollector.drawAndSendImage(data);
            }
            catch (err) {
                this.handleExceptionInPublicApi(err);
            }
        });
    }
    createScreenshot(data) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.state !== PlasmState.started) {
                throw new PlasmIntegrationError({ msg: "failed create screenshot.fo Plasm not started" });
            }
            try {
                const screenshotsCollector = this.configuredCollectors.find((collector) => collector.id === CollectorId.screen);
                if (screenshotsCollector == null) {
                    throw new PlasmIntegrationError({ msg: "screenshot collector not configured" });
                }
                yield screenshotsCollector.drawAndSendImage(data);
            }
            catch (err) {
                this.handleExceptionInPublicApi(err);
            }
        });
    }
    setApplicationMetadata() {
        return __awaiter(this, void 0, void 0, function* () {
            const { applicationMetadata, clientToken, plasmApiUrl } = this.options;
            if (applicationMetadata == null) {
                return;
            }
            const url = `${plasmApiUrl}/applications/set-metadata`;
            utils.request({
                authToken: clientToken,
                method: "POST",
                url,
                payload: applicationMetadata,
            });
        });
    }
    refreshCollector(id) {
        return __awaiter(this, void 0, void 0, function* () {
            const existingCollector = this.configuredCollectors.find((c) => c.id === id);
            if (existingCollector == null) {
                this.logger.warn(`[PLASM] collector "${id}" not found`);
                return null;
            }
            const { options } = existingCollector;
            try {
                yield existingCollector.stop();
                const refreshedCollector = this.createCollector(id, options);
                const setupResult = yield refreshedCollector.setup();
                this.configuredCollectors = this.configuredCollectors.map((collector) => {
                    if (collector.id === id) {
                        return refreshedCollector;
                    }
                    return collector;
                });
                if (this.state === PlasmState.started) {
                    yield refreshedCollector.getReady();
                    yield refreshedCollector.start();
                }
                return setupResult;
            }
            catch (err) {
                Sentry.captureError(err);
                return null;
            }
        });
    }
}
export default Plasm;
window.Plasm = Plasm;
